import { useCallback, useContext, useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { InstanceContext } from '@/contexts/InstanceContext';
import { ActivityContext } from '@/contexts/ActivityContext';
import { TimerCircle, useChildTokens, useRoutineStepLabels } from '@/components/child';
import { getCurrentStepIndex, getRoutineSteps, isLockedUntilItsDay } from '@/utils/routine';
import RoutineStepBricks from './RoutineStepBricks';
import {
  clearCarry,
  currentCarrySeconds,
  getShownSeconds,
  getStepBudgetSeconds,
  nextCarrySeconds,
  readCarryStack,
  readRemainingSeconds,
  writeCarryStack,
  type CarryStack,
} from '@/utils/routineCarry';

/**
 * RoutineStepCard — rutinen som brukaren möter: ETT steg i taget.
 *
 * Kortet visar rutinens namn, var i kedjan man är, steget självt, stegets EGEN
 * nedräkning och en Nästa-knapp.
 *
 * "Var i kedjan man är" ritas som LEGOBITAR i överkant (RoutineStepBricks) — en bit per
 * steg, som fylls när steget klaras av. Det ersatte textraden "steg 2 av 5", som
 * förutsatte att brukaren läser siffror; alla barn gör inte det och behöver ändå se hur
 * långt det är kvar. Bitarna säger bara HUR MÅNGA steg som återstår, inte vilka, så
 * kortet håller fortfarande fast vid att bara EN sak är aktuell åt gången. Handledaren
 * kan slå på stegens namn i bitarna för brukare som har nytta av dem.
 *
 * Nästa = bocka av steget man står på. Läget ligger alltså i instansens
 * checkedSubtasks (se utils/routine.ts) och behöver ingen egen lagring — det följer
 * med till dagsvyn och veckovyn och överlever en omladdning. På sista steget står
 * det Klar i stället, och då markeras rutinen som genomförd.
 *
 * Timern är samma TimerCircle som barnvyn använder, så brukarens val av
 * visningsform (ring/stapel/klocka/digital) gäller även här. Varje steg får en EGEN
 * nyckel: annars hade nedräkningen fortsatt från föregående steg när man gick vidare.
 *
 * Kortet läser InstanceContext/ActivityContext själv och kan därför användas i både
 * barnets och den vuxna brukarens aktivitetsvy — båda monterar samma providers.
 */

/** Nyckeln för ett stegs sparade nedräkning. Unik per instans OCH steg. */
export const stepTimerId = (activityInstanceId: number, subtaskId: number): string =>
  `${activityInstanceId}-step-${subtaskId}`;

type RoutineStepCardProps = {
  /** Timerns accentfärg. Utelämnad = temats primärfärg, alltså valt färgschema. */
  color?: string;
  /**
   * Visa rutinens namn i kortet. Stängs av där namnet redan står som rubrik ovanför
   * kortet (barnets aktivitetsvy) — samma namn två gånger är brus i en vy vars hela
   * syfte är att visa en enda sak.
   */
  showName?: boolean;
};

const RoutineStepCard = ({ color, showName = true }: RoutineStepCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = useChildTokens();
  const {
    instance,
    isStarted,
    timerPaused,
    setTimerPaused,
    startInstance,
    completeInstance,
    toggleSubtask,
  } = useContext(InstanceContext);
  const { activity } = useContext(ActivityContext);
  const stepLabels = useRoutineStepLabels();

  const steps = useMemo(() => getRoutineSteps(activity), [activity]);
  const stepIndex = getCurrentStepIndex(steps, instance.checkedSubtasks);
  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const isRunning = isStarted && !timerPaused;
  // Dagslåset (V011). Kortet kan monteras utanför ActivityInProgress, så spärren står
  // här också — och inte bara som en dold knapp: att gå vidare i en rutin som inte
  // fått börja är samma sak som att starta den.
  const lockedUntilItsDay = isLockedUntilItsDay(activity, instance);

  // Tid till godo (eller skuld) från föregående steg. Läses ur localStorage vid
  // montering så att den överlever en omladdning mitt i rutinen, och hålls därefter i
  // state eftersom den ändras när brukaren går vidare eller backar.
  const [carryStack, setCarryStack] = useState<CarryStack>(() =>
    readCarryStack(instance.activityInstanceId),
  );
  const carrySeconds = currentCarrySeconds(carryStack);

  const budgetSeconds = step ? getStepBudgetSeconds(step.durationMinutes, carrySeconds) : 0;
  const shownSeconds = getShownSeconds(budgetSeconds);
  // Avrundat mot noll: 90 sekunder till godo är "1 min extra", 30 sekunder ingenting.
  const carryMinutes = Math.trunc(carrySeconds / 60);

  const handleToggleTimer = useCallback(() => {
    if (lockedUntilItsDay) return;
    if (!isStarted) {
      startInstance();
      return;
    }
    setTimerPaused(!timerPaused);
  }, [lockedUntilItsDay, isStarted, startInstance, timerPaused, setTimerPaused]);

  const handleNext = useCallback(() => {
    if (!step) return;
    const timerKey = stepTimerId(instance.activityInstanceId, step.subtaskId);
    // Hur mycket av stegets tid som blev över (positivt) eller drogs över (negativt),
    // plus en skuld som det här steget inte räckte till för att betala av.
    const remaining = readRemainingSeconds(timerKey, shownSeconds);
    const carried = nextCarrySeconds(remaining, budgetSeconds);

    // Stegets nedräkning är förbrukad när steget lämnas; utan städningen hade en ny
    // dag börjat på den gamla resttiden (staten lever i localStorage).
    localStorage.removeItem(`timer-${timerKey}`);
    toggleSubtask(step.subtaskId);

    if (isLastStep) {
      // Rutinen är genomgången: ingen kvarvarande tid att bära någonstans.
      clearCarry(instance.activityInstanceId);
      setCarryStack([]);
      completeInstance();
      return;
    }
    const nextStack = [...carryStack, carried];
    writeCarryStack(instance.activityInstanceId, nextStack);
    setCarryStack(nextStack);
  }, [
    step,
    instance.activityInstanceId,
    shownSeconds,
    budgetSeconds,
    carryStack,
    toggleSubtask,
    isLastStep,
    completeInstance,
  ]);

  /**
   * Ett steg tillbaka. Att trycka fel ska inte låsa in brukaren i resten av rutinen —
   * knappen visas bara när det FINNS ett föregående steg, och gör exakt tvärtom mot
   * Nästa: den avbockningen blir det aktuella steget igen.
   */
  const handleBack = useCallback(() => {
    const previous = steps[stepIndex - 1];
    if (!previous) return;
    // Ta bort tiden som bars över FRÅN det steget, annars hade steget räknat sitt eget
    // överskott en gång till när det görs om.
    const restoredStack = carryStack.slice(0, -1);
    writeCarryStack(instance.activityInstanceId, restoredStack);
    setCarryStack(restoredStack);
    // Stegets gamla nedräkning ska inte återuppstå — det görs om från sin fulla tid.
    localStorage.removeItem(
      `timer-${stepTimerId(instance.activityInstanceId, previous.subtaskId)}`,
    );
    toggleSubtask(previous.subtaskId);
  }, [steps, stepIndex, carryStack, instance.activityInstanceId, toggleSubtask]);

  if (!step) return null;

  return (
    <Box
      sx={{
        bgcolor: tokens.card,
        border: '1px solid',
        borderColor: tokens.border,
        borderRadius: '18px',
        p: '18px 16px',
        boxShadow: tokens.shadow,
      }}
    >
      <Stack spacing={1.5} alignItems="center">
        <RoutineStepBricks steps={steps} currentIndex={stepIndex} labels={stepLabels} />

        {showName && (
          <Typography
            sx={{ fontWeight: 700, fontSize: '0.8rem', color: tokens.textSoft, letterSpacing: 0.2 }}
          >
            {instance.activityName}
          </Typography>
        )}

        {/* Steget självt — sidans största text, för det är det som ska göras nu. */}
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: '1.6rem',
            color: tokens.text,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {step.subtaskText}
        </Typography>

        {/* Steget räknar ner sin BUDGET: egen tid ± tiden som bars över från förra
            steget. key:en är stegets id — utan den hade timerstaten (useState inuti
            useTimerState) levt vidare från föregående steg, eftersom komponenten står
            på samma plats i trädet och bara får nya props. */}
        <TimerCircle
          key={step.subtaskId}
          minutes={Math.floor(shownSeconds / 60)}
          seconds={shownSeconds % 60}
          isRunning={isRunning}
          timerId={stepTimerId(instance.activityInstanceId, step.subtaskId)}
          onToggle={handleToggleTimer}
          color={color ?? theme.palette.primary.main}
        />

        {/* Överförd tid, i hela minuter. Under en minut nämns inte: en rad som säger
            "0 min extra" är brus i en vy som ska visa en enda sak. */}
        {carryMinutes !== 0 && (
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              textAlign: 'center',
              color: carryMinutes > 0 ? 'success.main' : tokens.attentionMain,
            }}
          >
            {carryMinutes > 0
              ? t('routine.carryBonus', { minutes: carryMinutes })
              : t('routine.carryDeficit', { minutes: Math.abs(carryMinutes) })}
          </Typography>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={lockedUntilItsDay}
          fullWidth
          sx={{
            borderRadius: '14px',
            py: 1.5,
            fontWeight: 800,
            fontSize: '1.05rem',
            textTransform: 'none',
          }}
        >
          {isLastStep ? t('routine.finish') : t('routine.next')}
        </Button>

        {stepIndex > 0 && (
          <Button
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 700, color: tokens.textSoft }}
          >
            {t('routine.back')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default RoutineStepCard;
